import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { createDogAttribute, updateDogAttribute } from '../services/dogAttributeApi';

const ATTRIBUTE_TYPES = ['BREED', 'SIZE', 'ENERGY_LEVEL'];

/**
 * Dialog for creating or editing a dog attribute.
 *
 * Props:
 *   open         {boolean}
 *   attribute    {Object|null}  — null → create mode; object → edit mode (pre-filled)
 *   onClose      {() => void}
 *   onSaved      {(saved: Object) => void}  — called after successful save
 */
const DogAttributeForm = ({ open, attribute, onClose, onSaved }) => {
  const isEditing = attribute != null;

  const [type, setType]       = useState('BREED');
  const [nameEn, setNameEn]   = useState('');
  const [nameHe, setNameHe]   = useState('');
  const [nameEs, setNameEs]   = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Populate fields whenever the dialog opens or the target attribute changes
  useEffect(() => {
    if (open) {
      setType(attribute?.type ?? 'BREED');
      setNameEn(attribute?.nameEn ?? '');
      setNameHe(attribute?.nameHe ?? '');
      setNameEs(attribute?.nameEs ?? '');
      setError('');
    }
  }, [open, attribute]);

  const handleSubmit = async () => {
    if (!nameEn.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        nameEn: nameEn.trim(),
        nameHe: nameHe.trim() || null,
        nameEs: nameEs.trim() || null,
      };
      let saved;
      if (isEditing) {
        saved = await updateDogAttribute(attribute.id, payload);
      } else {
        saved = await createDogAttribute({ type, ...payload });
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? `Edit Attribute #${attribute.id}` : 'Add Attribute'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth disabled={isEditing}>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value)}
            >
              {ATTRIBUTE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="English name"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            required
            fullWidth
            autoFocus={!isEditing}
            error={!nameEn.trim() && saving}
            helperText={!nameEn.trim() && saving ? 'Required' : ''}
          />

          <TextField
            label="Hebrew name (optional)"
            value={nameHe}
            onChange={(e) => setNameHe(e.target.value)}
            fullWidth
            inputProps={{ dir: 'rtl' }}
          />

          <TextField
            label="Spanish name (optional)"
            value={nameEs}
            onChange={(e) => setNameEs(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !nameEn.trim()}
        >
          {saving ? 'Saving…' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DogAttributeForm;
